import { createSlice } from '@reduxjs/toolkit';
import {
  BusinessHourSlot,
  PaymentSetting,
  ReservationInquiry,
  ReservationMenu,
  ReservationSeatType,
  ReservationSetting,
  ReservationStoreImages,
  ReservationStoreInfo,
  SpecialDay,
  StripeConnectionStatus,
} from '../../types/reservation';
import {
  fetchBusinessHours,
  fetchInquiries,
  fetchNotificationEmails,
  fetchPaymentSetting,
  fetchReservationMenus,
  fetchReservationSetting,
  fetchReservationStoreImages,
  fetchReservationStoreInfo,
  fetchSeatTypes,
  fetchSpecialDays,
  fetchStripeStatus,
  saveNotificationEmails,
  savePaymentSetting,
  saveReservationSetting,
  saveReservationStoreImages,
  saveReservationStoreInfo,
} from './reservationSettingsApi';

interface ReservationSettingsState {
  setting: ReservationSetting | null;
  settingLoading: boolean;
  /**
   * 受付設定が一度も保存されていない（BE が 404 を返した）場合に true。
   * true の間は公開予約の受付が開始できないため、UI で保存を促す警告と保存導線を表示する。
   * 保存（saveReservationSetting）成功で false になる。取得前は false 扱い。
   */
  settingNeverSaved: boolean;
  seatTypes: ReservationSeatType[];
  businessHours: BusinessHourSlot[];
  specialDays: SpecialDay[];
  menus: ReservationMenu[];
  inquiries: ReservationInquiry[];
  paymentSetting: PaymentSetting | null;
  stripeStatus: StripeConnectionStatus | null;
  notificationEmails: string[];
  storeInfo: ReservationStoreInfo | null;
  /** 店舗写真（ヒーロー+ギャラリー・フィードバック2） */
  storeImages: ReservationStoreImages | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReservationSettingsState = {
  setting: null,
  settingLoading: false,
  settingNeverSaved: false,
  seatTypes: [],
  businessHours: [],
  specialDays: [],
  menus: [],
  inquiries: [],
  paymentSetting: null,
  stripeStatus: null,
  notificationEmails: [],
  storeInfo: null,
  storeImages: null,
  loading: false,
  error: null,
};

const reservationSettingsSlice = createSlice({
  name: 'reservationSettings',
  initialState,
  reducers: {
    clearSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReservationSetting.pending, (state) => {
        state.settingLoading = true;
        state.error = null;
      })
      .addCase(fetchReservationSetting.fulfilled, (state, action) => {
        state.setting = action.payload.setting;
        state.settingNeverSaved = !action.payload.persisted;
        state.settingLoading = false;
      })
      .addCase(fetchReservationSetting.rejected, (state, action) => {
        state.settingLoading = false;
        state.error = action.payload as string;
      });
    builder.addCase(saveReservationSetting.fulfilled, (state, action) => {
      state.setting = action.payload;
      // 保存が成功した時点で行が作成され、以降は「保存済み」になる
      state.settingNeverSaved = false;
    });

    builder.addCase(fetchSeatTypes.fulfilled, (state, action) => {
      state.seatTypes = action.payload;
    });
    builder.addCase(fetchBusinessHours.fulfilled, (state, action) => {
      state.businessHours = action.payload;
    });
    builder.addCase(fetchSpecialDays.fulfilled, (state, action) => {
      state.specialDays = action.payload;
    });
    builder.addCase(fetchReservationMenus.fulfilled, (state, action) => {
      state.menus = action.payload;
    });
    builder.addCase(fetchInquiries.fulfilled, (state, action) => {
      state.inquiries = action.payload;
    });

    builder.addCase(fetchPaymentSetting.fulfilled, (state, action) => {
      state.paymentSetting = action.payload;
    });
    builder.addCase(savePaymentSetting.fulfilled, (state, action) => {
      state.paymentSetting = action.payload;
    });
    builder.addCase(fetchStripeStatus.fulfilled, (state, action) => {
      state.stripeStatus = action.payload;
    });

    builder.addCase(fetchNotificationEmails.fulfilled, (state, action) => {
      state.notificationEmails = action.payload.emails;
    });
    builder.addCase(saveNotificationEmails.fulfilled, (state, action) => {
      state.notificationEmails = action.payload.emails;
    });
    builder.addCase(fetchReservationStoreInfo.fulfilled, (state, action) => {
      state.storeInfo = action.payload;
    });
    builder.addCase(saveReservationStoreInfo.fulfilled, (state, action) => {
      // PUT ボディには読み取り専用の storeName/address が含まれないため、既存値を保持してマージする
      state.storeInfo = { ...state.storeInfo, ...action.payload };
    });
    builder.addCase(fetchReservationStoreImages.fulfilled, (state, action) => {
      state.storeImages = action.payload;
    });
    builder.addCase(saveReservationStoreImages.fulfilled, (state, action) => {
      state.storeImages = action.payload;
    });
  },
});

export const { clearSettingsError } = reservationSettingsSlice.actions;

export default reservationSettingsSlice.reducer;
